import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../../../hooks/useImagesLoaded';
import { COLOR_WHITE, COLOR_STEAK_SAUCE, COLOR_BLACK_STANDARD } from '../../../colors';
import { FUTURABOOK } from '../../../fonts';
import { addCommas } from '../../../../utils';
import ListingStatusLabel from './ListingStatusLabel';

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    flexContainer: {
        display: 'flex',
        minHeight: '100%',
        position: 'relative',
        fontFamily: ['futurabook', 'Arial', 'Helvetica', 'sans-serif'],
        fontWeight: 500,
    },
    leftWrap: {
        flex: 1,
        boxSizing: 'border-box',
        padding: '24px 24px 82px 24px',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minWidth: 492,
        marginRight: 12,
        backgroundColor: COLOR_STEAK_SAUCE,
        position: 'relative',
    },
    shadowGradient: {
        position: 'absolute',
        width: '100%',
        height: 360,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%)',
    },
    contentWrap: {
        fontSize: '20px',
        lineHeight: '24px',
        color: COLOR_WHITE,
        textAlign: 'center',
        minWidth: '100%',
        zIndex: 2,
    },
    rightWrap: {
        flex: 1,
        boxSizing: 'border-box',
        minHeight: '100%',
        textAlign: 'left',
        position: 'relative',
    },
    photoBlock: {
        width: '100%',
        height: 267,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: COLOR_STEAK_SAUCE,
    },
    complianceWrap: {
        boxSizing: 'border-box',
        width: '100%',
        fontSize: '10px',
        lineHeight: '14px',
        letterSpacing: '0.4px',
        zIndex: 444,
        backgroundColor: COLOR_BLACK_STANDARD,
        padding: '14px 24px 14px 24px',
        color: COLOR_WHITE,
    },
    disclaimerWrap: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    lrLogo: {
        display: 'block',
        height: '12px',
    },
    logoWrapper: {
        display: 'flex',
        alignItems: 'flex-end',
    },
    realtorLogo: {
        marginLeft: '20px',
        height: '24px',
    },
    equalHousingLogo: {
        marginLeft: '20px',
        height: '24px',
    },
    fixedFooter: {
        position: 'absolute',
        left: 0,
        bottom: 0,
        zIndex: 9999,
        width: '100%',
    }
}));

export default function Flourish({
    templateContent = {},
    currentFrame = 1,
    onImagesLoaded = null,
}) {
    const classes = useStyles();
    const {
        hero,
        address,
        city,
        state,
        photos,
        bedrooms,
        bathrooms,
        home_size,
        price_amount,
        open_houses = [],
        listing_status_label,
        realtor_logo_required = false,
    } = templateContent || {};
    const heroImage = (photos && photos.length) ? photos[0] : hero;
    const sqft = parseInt((home_size || '').replace(/\D/g, ''), 10) > 0 ? parseInt(home_size.replace(/\D/g, ''), 10) : null;
    const openHouses = open_houses || [];
    const loadedBgs = useImagesLoaded((photos && photos.length) ? photos : [heroImage]);
    if(!heroImage) {
        window.isLoaded = true;
    }

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
        return ()=> {
            window.isLoaded = false;
        };
    }, [loadedBgs, onImagesLoaded]);

    const display_address = address?.length > 0;

    return (
        <>
            <link rel="stylesheet" href={FUTURABOOK} />

            <div className={classes.flexContainer}>

                {/* Left Block */}

                <div className={classes.leftWrap}
                    style={{
                        backgroundImage: `url("${heroImage}")`
                    }}
                >
                    <div className={classes.shadowGradient}></div>
                    <div className={classes.contentWrap}>
                        <div>
                            <ListingStatusLabel statusLabel={`${listing_status_label || 'Just Listed'}`} />
                        </div>

                        {openHouses.length > 0 && (
                            openHouses.map((openHouse)=> {
                                return (
                                    <div key={`${openHouse.date}${openHouse.time}`}>
                                        {openHouse.date} at {openHouse.time}
                                    </div>
                                );
                            })
                        )}

                        {!display_address && city && openHouses.length > 0 && (
                            <div>
                                in {city}, {state}
                            </div>
                        )}

                        {display_address && openHouses.length > 0 && (
                            address.map((addressLine)=> <div key={addressLine}>{addressLine}</div>)
                        )}

                        <div style={{marginTop: 2}}>
                            {price_amount && (<span>{`$${addCommas(price_amount)}`}</span>)}{' '}
                            {price_amount && (bedrooms > 0 || bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                            {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                            {bedrooms > 0 && (bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                            {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                            {bathrooms > 0 && (sqft && (<span>&bull;</span>))}{' '}

                            {sqft && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                        </div>

                        {!display_address && city && openHouses.length === 0 && (
                            <div>
                                in {city}, {state}
                            </div>
                        )}

                        {display_address && openHouses.length === 0 && (
                            address.map((addressLine)=> <div key={addressLine}>{addressLine}</div>)
                        )}
                    </div>
                </div>


                {/* Right Block */}

                <div className={classes.rightWrap}>
                    <div className={classes.photoBlock} style={{backgroundImage: `url('${photos[1]}')`, marginBottom: 12}}></div>
                    <div className={classes.photoBlock} style={{backgroundImage: `url('${photos[2]}')`}}>
                        <div className={classes.shadowGradient} style={{height: 100}}></div>
                    </div>
                </div>

                {/* Compliance */}
                <div className={classes.fixedFooter}>
                    <div className={classes.complianceWrap}>
                        <div className={classes.disclaimerWrap}>
                            <div>
                                <img src="./images/lr-logo-disclaimer-white.svg" className={classes.lrLogo} alt="LR Logo" />
                                <div>&copy; ListReports {COPYRIGHT_YEAR}, all rights reserved</div>
                            </div>
                            <div>
                                This information is from third-party sources and its accuracy cannot be guaranteed
                            </div>
                            <div className={classes.logoWrapper}>
                                {realtor_logo_required && 
                                    <img src="./images/realtor-logo-white.svg" className={classes.realtorLogo} alt="Realtor logo" />
                                }
                                <img src="./images/equal-housing-logo-white.svg" className={classes.equalHousingLogo} alt="Equal Housing logo" />
                            </div>
                        </div>
                    </div>
                </div> 

            </div>
        </>
    );
}
